import React from 'react';
import { Text, View, ViewStyle } from 'react-native';
import { radius, spacing, type, useAppTheme } from '@/theme';
import { BookingStatus } from '@/data/types';
import { ScaledSheet } from '@/theme/responsive';
import { trText } from '@/i18n/plain';
import { useLanguage } from '@/i18n';

interface BadgeProps {
  label: string;
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'error' | 'info' | 'dark';
  style?: ViewStyle;
}

export function Badge({ label, tone = 'neutral', style }: BadgeProps) {
  const { colors } = useAppTheme();

  const toneMap = {
    neutral: { bg: colors.surfaceMuted, fg: colors.textSecondary },
    accent: { bg: colors.accentSoft, fg: colors.accentDark },
    success: { bg: colors.successSoft, fg: colors.success },
    warning: { bg: colors.warningSoft, fg: colors.warning },
    error: { bg: colors.errorSoft, fg: colors.error },
    info: { bg: colors.infoSoft, fg: colors.info },
    dark: { bg: colors.ink, fg: colors.textOnDark },
  };

  const palette = toneMap[tone];
  return (
    <View style={[styles.base, { backgroundColor: palette.bg }, style]}>
      <Text style={[type.caption, { color: palette.fg }]}>{label}</Text>
    </View>
  );
}

const statusMap: Record<BookingStatus, { label: string; tone: BadgeProps['tone'] }> = {
  PENDING: { get label() { return trText('Awaiting approval', 'অনুমোদনের অপেক্ষায়'); }, tone: 'warning' },
  CONFIRMED: { get label() { return trText('Confirmed', 'কনফার্মড'); }, tone: 'info' },
  IN_PROGRESS: { get label() { return trText('In Progress', 'অনগোয়িং'); }, tone: 'accent' },
  COMPLETED: { get label() { return trText('Completed', 'কমপ্লিট'); }, tone: 'success' },
  CANCELLED: { get label() { return trText('Cancelled', 'ক্যান্সেল'); }, tone: 'error' },
  NO_SHOW: { get label() { return trText('No Show', 'আসেননি'); }, tone: 'neutral' },
};

export function StatusBadge({ status, style }: { status: BookingStatus; style?: ViewStyle }) {
  useLanguage(); // redraw when the language changes (trText is read at call time)
  const { label, tone } = statusMap[status];
  return <Badge label={label} tone={tone} style={style} />;
}

const styles = ScaledSheet.create({
  base: { paddingHorizontal: spacing.sm, paddingVertical: 4, borderRadius: radius.xs, alignSelf: 'flex-start' },
});
